import { useTranslation } from 'react-i18next'
import { ShoppingCart } from 'lucide-react'
import { formatCurrency } from '../utils/helpers'
import type { InvoiceLine, OrderLine, ProposalLine } from '../types'

type AnyLine = InvoiceLine | OrderLine | ProposalLine

interface Props {
  lines: AnyLine[]
  /** If set, only the first `maxRows` lines are shown with a "X more" note */
  maxRows?: number
  /** If provided, shows an "Add to cart" button per line (only for lines with product_ref) */
  onAddToCart?: (line: AnyLine, qty: number) => void
}

/**
 * Generic line items table used in InvoiceDetail, OrderDetail, and ProposalDetail.
 */
export default function LineItemsTable({ lines, maxRows, onAddToCart }: Props) {
  const { t } = useTranslation()

  if (!lines || lines.length === 0) {
    return <p className="text-sm text-gray-500 italic">{t('lineItems.noLines')}</p>
  }

  const visible = maxRows ? lines.slice(0, maxRows) : lines
  const extra = maxRows && lines.length > maxRows ? lines.length - maxRows : 0

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colRef')}
            </th>
            <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colDescription')}
            </th>
            <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colQty')}
            </th>
            <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colUnitPrice')}
            </th>
            <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colVat')}
            </th>
            <th className="text-right py-2 px-3 text-xs font-medium text-gray-500 uppercase tracking-wider">
              {t('lineItems.colTotal')}
            </th>
            {onAddToCart && <th className="py-2 px-3 w-10" />}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {visible.map((line, idx) => {
            const label = (line as AnyLine).product_label || (line as AnyLine).description || ''
            const ref = (line as AnyLine).product_ref || ''
            const hasProduct = !!(
              ref ||
              (line as InvoiceLine).product_id ||
              (line as OrderLine).product_id
            )
            return (
              <tr key={(line as AnyLine).id ?? idx} className="hover:bg-gray-50">
                <td className="py-2 px-3 text-gray-600 font-mono whitespace-nowrap">
                  {ref || '—'}
                </td>
                <td className="py-2 px-3 text-gray-800 max-w-xs truncate" title={label}>
                  {label || '—'}
                </td>
                <td className="py-2 px-3 text-right text-gray-700">{line.qty}</td>
                <td className="py-2 px-3 text-right text-gray-700">
                  {formatCurrency(line.unit_price)}
                </td>
                <td className="py-2 px-3 text-right text-gray-500">{line.vat_rate}%</td>
                <td className="py-2 px-3 text-right font-medium text-gray-900">
                  {formatCurrency(line.total_excl_tax)}
                </td>
                {onAddToCart && (
                  <td className="py-2 px-3 text-center">
                    {hasProduct && (
                      <button
                        onClick={() => onAddToCart(line, line.qty)}
                        title={t('lineItems.addToCart')}
                        className="p-1 text-gray-400 hover:text-primary-600 hover:bg-primary-50 rounded transition-colors"
                      >
                        <ShoppingCart className="w-4 h-4" />
                      </button>
                    )}
                  </td>
                )}
              </tr>
            )
          })}
        </tbody>
      </table>
      {extra > 0 && (
        <p className="text-xs text-gray-500 mt-2 px-3 italic">
          {t('lineItems.moreLines', { count: extra })}
        </p>
      )}
    </div>
  )
}
